import { Link } from 'react-router-dom';
import { formatWineType, type Wine } from '@wineryxplorer/shared';
import { ManagementRowActions } from '../ui/ManagementRowActions';

// One wine in the venue-wines manager. Deliberately the same row grammar as the
// public `components/WineRow.tsx` — vintage gutter, serif name, dot-joined
// sub-line, price on the right, hairline rule between rows — so the management
// list reads as the same product as the wine catalogue rather than as a
// generic card dashboard (#181). The parent still owns the query, the delete
// mutation and its confirm dialog.
interface VenueWineRowProps {
  wine: Wine;
  isDeleting: boolean;
  onEdit: (wineId: string) => void;
  onDelete: (wineId: string) => void;
}

export const VenueWineRow = ({ wine, isDeleting, onEdit, onDelete }: VenueWineRowProps) => {
  // The venue is implicit in this list, so the sub-line carries the things that
  // actually distinguish one of its wines from another.
  const subLine = [formatWineType(wine.wineType), wine.varietal, wine.appellation]
    .filter((part): part is string => !!part)
    .join(' · ');

  return (
    <div className="flex items-center gap-4 border-b border-gray-200 px-4 py-3.5 transition-colors last:border-b-0 hover:bg-gray-100">
      <span className="min-w-[44px] flex-shrink-0 text-base text-gray-600">
        {wine.vintage ?? '—'}
      </span>

      <Link to={`/wines/${wine._id}`} className="group min-w-0 flex-1">
        <div className="truncate font-serif text-lg leading-tight text-gray-900 group-hover:text-primary-700">
          {wine.name}
        </div>
        {subLine && <div className="mt-0.5 truncate text-sm text-gray-600">{subLine}</div>}
      </Link>

      <span className="min-w-[60px] flex-shrink-0 text-right text-base text-gray-700">
        {wine.price !== undefined ? `$${Math.round(wine.price)}` : ''}
      </span>

      <ManagementRowActions
        itemId={wine._id}
        itemName={wine.name}
        isDeleting={isDeleting}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </div>
  );
};
